// ui/src/components/padrekko/qc/GatedAction.jsx
//
// An action that may be unavailable, and says why.
//
// Three props. That is the whole component, and it is deliberate: a primitive
// that grows a prop to fit each new consumer is not a primitive, it is a
// second implementation with extra steps. If a caller needs a fourth prop, the
// right move is to question the caller.
//
//   enabled   whether the wrapped control can be used
//   reason    why not, shown on hover when disabled
//   children  exactly one control (a <button>, in both current callers)
//
// ── WHY THE TITLE IS ON THE WRAPPER, NOT THE BUTTON ──────────────────────────
//
// A disabled <button> does not fire mouse events in any browser, so a `title`
// on the button itself never appears — the tooltip explaining WHY the control
// is unavailable is invisible in exactly the state it exists to explain. The
// wrapping <span> is not disabled, so it receives hover and shows the reason.
//
// That bug is easy to write and nearly impossible to notice, because the
// disabled styling looks correct and the missing explanation is an absence.
// Centralizing it here is most of this component's value.
//
// ── WHAT THIS IS NOT FOR ─────────────────────────────────────────────────────
//
// Hidden-when-not-permitted (AttachmentSection) and stage-level explanation
// (CaseBanner) are different semantics, not variants of this one. Hiding a
// control and disabling it say different things to a user: hidden means "not
// part of your job", disabled means "yours, but not now". Folding them in here
// would be the wrong consolidation.

import { Children, cloneElement, isValidElement } from "react";

export default function GatedAction({ enabled, reason, children }) {
  // Exactly one child, so `disabled` has an unambiguous target. Children.only
  // throws on zero or many, which is the right time to find out.
  const child = Children.only(children);
  if (!isValidElement(child)) return child;

  if (enabled) {
    // Pass through untouched when enabled — the control keeps its own title
    // and its own styling, and nothing here interferes.
    return cloneElement(child, { disabled: false });
  }

  return (
    <span
      // The reason lives here so it survives the child being disabled.
      title={reason || undefined}
      className="inline-flex cursor-not-allowed"
      // Disabled controls are not focusable, so without this the reason is
      // unreachable to a screen reader as well as invisible to a mouse.
      aria-label={reason || undefined}
    >
      {cloneElement(child, { disabled: true })}
    </span>
  );
}
